import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Container from '../ui/Container';
import ProductCard from './ProductCard';
import { products, themes, getProductsByGroup } from '../../data/products';
import './ProductsShowcase.css';

const GROUP_META = {
  live: { title: '지금 서비스 중', desc: '스토어에서 받아 바로 쓸 수 있습니다.' },
  dev: { title: '다음에 나올 것', desc: '지금 만들고 있는 서비스입니다.' },
  suite: { title: '실험 모음, 뚝딱', desc: '작게 만들어 본 도구들을 한 앱에 모았습니다.' },
};

/**
 * @param {object} props
 * @param {boolean} [props.hideHeader]
 * @param {'home'|'page'} [props.layout]
 *   home — 지금 서비스 중(2열) / 다음에 나올 것(2열) / 뚝딱(가로형)
 *   page — 테마 필터 + 서비스 중(3열, 뚝딱 포함) / 다음에 나올 것(2열)
 * @param {boolean} [props.showAllLink]
 */
const ProductsShowcase = ({ hideHeader = false, layout = 'home', showAllLink = false }) => {
  const [theme, setTheme] = useState('all');

  const sections = useMemo(() => {
    if (layout === 'page') {
      return [
        { key: 'live', ...GROUP_META.live, cols: 3, items: products.filter((p) => p.status === 'live') },
        { key: 'dev', ...GROUP_META.dev, cols: 2, items: products.filter((p) => p.status !== 'live') },
      ];
    }
    return [
      { key: 'live', ...GROUP_META.live, cols: 2, items: getProductsByGroup('live') },
      { key: 'dev', ...GROUP_META.dev, cols: 2, items: getProductsByGroup('dev') },
      { key: 'suite', ...GROUP_META.suite, cols: 1, items: getProductsByGroup('suite'), wide: true },
    ];
  }, [layout]);

  return (
    <section
      className={`ft-showcase${hideHeader ? ' ft-showcase--no-header' : ''}`}
      id="products"
    >
      <Container>
        {!hideHeader && (
          <header className="ft-showcase__head">
            <span className="ft-showcase__eyebrow">Products</span>
            <h2 className="ft-showcase__title">
              지금 만드는 것,
              <br />
              다음에 만들 것.
            </h2>
            <p className="ft-showcase__desc">
              여행, 건강·습관, 비즈니스 검증, 생활 도구 — 일상에서 쓰는 서비스를 만듭니다.
            </p>
          </header>
        )}

        {layout === 'page' && (
          <nav className="ft-showcase__filters" aria-label="테마 필터">
            <button
              type="button"
              className={`ft-showcase__chip${theme === 'all' ? ' is-active' : ''}`}
              onClick={() => setTheme('all')}
              aria-pressed={theme === 'all'}
            >
              전체
            </button>
            {themes.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`ft-showcase__chip${theme === t.key ? ' is-active' : ''}`}
                onClick={() => setTheme(t.key)}
                aria-pressed={theme === t.key}
              >
                {t.label}
              </button>
            ))}
          </nav>
        )}

        {sections.map((section) => {
          const items =
            layout === 'page' && theme !== 'all'
              ? section.items.filter((p) => p.theme === theme)
              : section.items;
          if (items.length === 0) return null;

          return (
            <div key={section.key} className="ft-showcase__group">
              <div className="ft-showcase__group-head">
                <h3 className="ft-showcase__group-title">{section.title}</h3>
                <p className="ft-showcase__group-desc">{section.desc}</p>
              </div>

              <div className={`ft-showcase__grid ft-showcase__grid--${section.cols}`}>
                {items.map((p, i) => (
                  <ProductCard
                    key={p.slug}
                    product={p}
                    index={i}
                    variant={section.wide ? 'wide' : 'default'}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {showAllLink && (
          <div className="ft-showcase__more">
            <Link to="/products" className="ft-showcase__more-btn">
              전체 프로덕트 보기
              <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
          </div>
        )}
      </Container>
    </section>
  );
};

export default ProductsShowcase;
